import {WhiteboardStore} from './'
import {TLBounds, Utils} from '@tldraw/core'
import {action, computed} from 'mobx'
import {guid} from '@utils'

export class WhiteboardSelectionStore {
  constructor(public store: WhiteboardStore) {}

  get selectedIds() {
    return this.store.myPageState.selectedIds
  }

  @computed get selectedBounds(): TLBounds {
    return Utils.getCommonBounds(this.selectedShapes.map(s => this.store.shapeUtilFor(s).getBounds(s)))
  }

  @computed get selectedShapes() {
    const {selectedIds, store: {page: {shapes}}} = this
    return selectedIds.map(id => shapes[id]).filter(s => !s?.isGenerated)
  }

  selectHistory = {
    stack: [[]] as string[][],
    pointer: 0
  }

  /* -------------------------------------------------- */
  /*                      Selection                     */
  /* -------------------------------------------------- */

  /**
   * Clear the selection history (undo/redo stack for selection).
   */
  @action private clearSelectHistory = (): this => {
    this.selectHistory.pointer = 0
    this.selectHistory.stack = [this.selectedIds]
    return this
  }

  /**
   * Adds a selection to the selection history (undo/redo stack for selection).
   */
  @action private addToSelectHistory = (ids: string[]): this => {
    if (this.selectHistory.pointer < this.selectHistory.stack.length) {
      this.selectHistory.stack = this.selectHistory.stack.slice(0, this.selectHistory.pointer + 1)
    }
    this.selectHistory.pointer++
    this.selectHistory.stack.push(ids)
    return this
  }

  /**
   * Set the current selection.
   * @param ids The ids to select
   * @param push Whether to add the ids to the current selection instead.
   */
  @action private setSelectedIds = (ids: string[], push = false) => {
    const {store: {myPageState, appState}} = this
    myPageState.selectedIds = push ? [...myPageState.selectedIds, ...ids] : [...ids]
  }

  /**
   * Undo the most recent selection.
   */
  @action undoSelect = (): this => {
    if (this.selectHistory.pointer > 0) {
      this.selectHistory.pointer--
      this.setSelectedIds(this.selectHistory.stack[this.selectHistory.pointer])
    }
    return this
  }

  /**
   * Redo the previous selection.
   */
  @action redoSelect = (): this => {
    if (this.selectHistory.pointer < this.selectHistory.stack.length - 1) {
      this.selectHistory.pointer++
      this.setSelectedIds(this.selectHistory.stack[this.selectHistory.pointer])
    }
    return this
  }

  /**
   * Select one or more shapes.
   * @param ids The shape ids to select.
   */
  @action select = (...ids: string[]): this => {
    const {store: {page: {shapes}}} = this
    ids = ids.filter(id => shapes[id])
    this.setSelectedIds(ids)
    this.addToSelectHistory(ids)
    return this
  }

  /**
   * Select all shapes on the page.
   */
  @action selectAll = (pageId = this.store.page.id): this => {
    const {setSelectedIds, store: {selectTool, appState, shapes}} = this
    // Select only shapes that are the direct child of the page
    setSelectedIds(
      shapes
        .filter((shape) => shape.parentId === pageId)
        .map((shape) => shape.id)
    )

    this.addToSelectHistory(this.selectedIds)

    if (appState.activeTool !== 'select') {
      selectTool('select')
    }

    return this
  }

  /**
   * Deselect any selected shapes.
   */
  @action selectNone = (): this => {
    this.setSelectedIds([])
    this.addToSelectHistory(this.selectedIds)
    return this
  }

  @action deselect = (id: string) => {
    this.select(...this.selectedIds.filter((oid) => oid !== id))
  }

  @computed get atLeastTwoNamedItemsSelected() {
    const {store: {selectedShapesByType}} = this
    return (selectedShapesByType['builderCard']?.length ?? 0) > 1
  }


  get page() { return this.store.page}
}


// WEBPACK FOOTER //
// ./src/modules/whiteboard/WhiteboardSelectionStore.ts